import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  Icon,
  Modal
} from '@clientbase/clientbase-library'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import useNotificationChannel, {
  NotificationChannelData
} from 'hooks/useNotificationChannel'
import { useSectionPermission } from 'hooks/usePermission'
import { registerRecordMerchant } from 'lib/merchant-group'
import { useRouter } from 'next/router'
import { parseCookies } from 'nookies'
import { notificationService } from 'services/notifications'
import {
  NotificationListItem,
  NotificationType
} from 'services/notifications/types'

import ModalChargeDetail from 'components/Modal/ModalChargeDetail'
import ModalInvoiceDetailsContent from 'components/Modal/ModalInvoiceDetails/ModalInvoiceDetailsContent'

import { toastProps } from 'utils/types/toast-props'

import { theme } from 'styles/theme'

import NotificationItem from './components/NotificationItem'
import NotificationSettings from './components/NotificationSettings'
import {
  navigateFromNotification,
  notificationHasNavigationTarget
} from './navigateFromNotification'
import * as S from './NotificationCenter.styles'

const SOUND_STORAGE_KEY = 'notification_sound_enabled'

const getSoundEnabled = (): boolean => {
  if (typeof window === 'undefined') return true
  const stored = localStorage.getItem(SOUND_STORAGE_KEY)
  return stored !== 'false'
}

const playNotificationSound = () => {
  try {
    const ctx = new AudioContext()
    const oscillator = ctx.createOscillator()
    const gain = ctx.createGain()

    oscillator.connect(gain)
    gain.connect(ctx.destination)

    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(880, ctx.currentTime)
    oscillator.frequency.setValueAtTime(1047, ctx.currentTime + 0.1)

    gain.gain.setValueAtTime(0.15, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3)

    oscillator.start(ctx.currentTime)
    oscillator.stop(ctx.currentTime + 0.3)
  } catch {
    // Audio not available
  }
}

type TypeFilter = NotificationType | null

const TYPE_FILTER_OPTIONS: {
  key: TypeFilter
  label: string
  color: string
}[] = [
  {
    key: 'billing',
    label: 'Cobranças',
    color: theme.palette.primary[100]
  },
  {
    key: 'contract',
    label: 'Contratos',
    color: theme.palette.info[300]
  },
  {
    key: 'customer',
    label: 'Clientes',
    color: theme.palette.success[300]
  },
  {
    key: 'system',
    label: 'Sistema',
    color: theme.palette.alert[300]
  },
  {
    key: 'accounts_payable',
    label: 'Contas a pagar',
    color: theme.palette.secondary[200]
  },
  {
    key: 'nfse',
    label: 'Notas Fiscais',
    color: theme.palette.secondary[100]
  },
  {
    key: 'alert',
    label: 'Alertas',
    color: theme.palette.error[300]
  }
]

const groupNotificationsByDate = (
  notifications: NotificationListItem[]
): { label: string; items: NotificationListItem[] }[] => {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 86400000)
  const weekAgo = new Date(today.getTime() - 7 * 86400000)

  const groups: Record<string, NotificationListItem[]> = {}
  const order: string[] = []

  notifications.forEach((n) => {
    const date = new Date(n.created_at)
    let label: string

    if (date >= today) {
      label = 'Hoje'
    } else if (date >= yesterday) {
      label = 'Ontem'
    } else if (date >= weekAgo) {
      label = 'Esta semana'
    } else {
      label = 'Anteriores'
    }

    if (!groups[label]) {
      groups[label] = []
      order.push(label)
    }
    groups[label].push(n)
  })

  return order.map((label) => ({ label, items: groups[label] }))
}

interface NotificationCenterProps {
  onOpenReportModal?: () => void
  onOpenBalanceModal?: (tab: string, disputeUuid?: string) => void
}

const NotificationCenter = ({
  onOpenReportModal,
  onOpenBalanceModal
}: NotificationCenterProps = {}) => {
  const { ['nextauth.merchant_user_uuid']: merchantUserUuid } = parseCookies()

  // O sino fica montado no Layout, ou seja, em TODA tela. Sem
  // `notifications:view` os endpoints respondem 403 e o interceptor do
  // DataService transforma isso no toast genérico de permissão, que aparecia
  // logo no login de quem tem perfil restrito (SC-10495). Não dispara a
  // request só pra cair no toast de erro.
  const canViewNotifications = useSectionPermission('notifications:view')

  const [isOpen, setIsOpen] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [allNotifications, setAllNotifications] = useState<
    NotificationListItem[]
  >([])
  const [filteredByTypeNotifications, setFilteredByTypeNotifications] =
    useState<NotificationListItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false)
  const [settingsHasChanges, setSettingsHasChanges] = useState(false)
  const [settingsIsSaving, setSettingsIsSaving] = useState(false)
  const [saveSettingsCallback, setSaveSettingsCallback] = useState<
    (() => Promise<void>) | null
  >(null)
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(null)
  const [unreadFilter, setUnreadFilter] = useState(false)
  const [chargeDetailUuid, setChargeDetailUuid] = useState<string | null>(null)
  const [nfseDetailUuid, setNfseDetailUuid] = useState<string | null>(null)

  const router = useRouter()

  // Ação de clique do toast em tempo real, mantida em ref para o callback do
  // canal (memoizado com []) sempre disparar a versão mais recente dos handlers,
  // sem recriar a assinatura do ActionCable. Preenchida abaixo do return dos
  // handlers de navegação.
  const toastClickRef = useRef<
    ((notification: NotificationChannelData) => void) | null
  >(null)

  const handleNotificationReceived = useCallback(
    (notification: NotificationChannelData) => {
      setAllNotifications((prev) => {
        const exists = prev.some((n) => n.uuid === notification.uuid)
        if (exists) return prev
        return [notification, ...prev]
      })
      setFilteredByTypeNotifications((prev) => {
        const exists = prev.some((n) => n.uuid === notification.uuid)
        if (exists) return prev
        return [notification, ...prev]
      })
      setUnreadCount((prev) => prev + 1)

      if (getSoundEnabled()) {
        playNotificationSound()
      }

      toast.info(
        <Stack gap={0.25}>
          <Typography
            variant="smBold"
            sx={{ fontSize: '13px', color: theme.palette.neutral[200] }}
          >
            {notification.title || 'Nova notificação'}
          </Typography>
          {notification.body && (
            <Typography
              variant="xsLight"
              sx={{
                fontSize: '12px',
                color: theme.palette.neutral[400],
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {notification.body}
            </Typography>
          )}
        </Stack>,
        {
          ...toastProps,
          autoClose: 5000,
          toastId: notification.uuid,
          ...(notificationHasNavigationTarget(notification) && {
            style: { cursor: 'pointer' },
            onClick: () => {
              toast.dismiss(notification.uuid)
              toastClickRef.current?.(notification)
            }
          })
        }
      )
    },
    []
  )

  useNotificationChannel({
    merchantUserUuid,
    onNotificationReceived: handleNotificationReceived,
    enabled: !!merchantUserUuid && canViewNotifications
  })

  const fetchNotifications = useCallback(
    async (
      page = 1,
      append = false,
      filterType?: TypeFilter,
      filterUnread?: boolean
    ) => {
      if (page === 1) {
        setIsLoading(true)
      } else {
        setIsLoadingMore(true)
      }

      try {
        const response = await notificationService.list({
          page,
          per_page: 20,
          ...(filterType ? { notification_type: filterType } : {}),
          ...(filterUnread ? { read: false } : {})
        })

        const items = response.notifications
        const setter =
          filterType || filterUnread
            ? setFilteredByTypeNotifications
            : setAllNotifications

        if (append) {
          setter((prev) => [...prev, ...items])
        } else {
          setter(items)
        }

        setUnreadCount(response.meta.unread_count)
        setCurrentPage(page)
        setHasMore(response.meta.next_page !== null)
      } catch (error) {
        console.error('Error fetching notifications:', error)
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    []
  )

  const fetchUnreadCount = useCallback(async () => {
    try {
      const count = await notificationService.getUnreadCount()
      setUnreadCount(count)
    } catch (error) {
      console.error('Error fetching unread count:', error)
    }
  }, [])

  useEffect(() => {
    if (!canViewNotifications) return
    fetchUnreadCount()
  }, [fetchUnreadCount, canViewNotifications])

  useEffect(() => {
    if (!canViewNotifications) return
    if (isOpen && allNotifications.length === 0) {
      fetchNotifications(1, false, null)
    }
  }, [
    isOpen,
    fetchNotifications,
    allNotifications.length,
    canViewNotifications
  ])

  const notifications =
    typeFilter || unreadFilter ? filteredByTypeNotifications : allNotifications

  const groupedNotifications = useMemo(
    () => groupNotificationsByDate(notifications),
    [notifications]
  )

  const handleToggleDrawer = () => {
    setIsOpen(!isOpen)
  }

  const handleCloseDrawer = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handleOpenSettings = () => {
    setIsSettingsOpen(true)
  }

  const handleCloseSettings = () => {
    setIsSettingsOpen(false)
    setSettingsHasChanges(false)
    setSaveSettingsCallback(null)
  }

  const handleSaveSettings = async () => {
    if (saveSettingsCallback) {
      setSettingsIsSaving(true)
      try {
        await saveSettingsCallback()
      } finally {
        setSettingsIsSaving(false)
      }
    }
  }

  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const loadStateRef = useRef({
    isLoadingMore: false,
    hasMore: false,
    currentPage: 1,
    typeFilter: null as TypeFilter,
    unreadFilter: false
  })

  loadStateRef.current = {
    isLoadingMore,
    hasMore,
    currentPage,
    typeFilter,
    unreadFilter
  }

  useEffect(() => {
    const el = loadMoreRef.current
    if (!el || !hasMore || isLoading) return

    // Find the scrollable parent (DrawerContent)
    let scrollRoot: HTMLElement | null = null
    let parent = el.parentElement
    while (parent) {
      const overflowY = window.getComputedStyle(parent).overflowY
      if (overflowY === 'auto' || overflowY === 'scroll') {
        scrollRoot = parent
        break
      }
      parent = parent.parentElement
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const state = loadStateRef.current
        if (
          entries[0].isIntersecting &&
          !state.isLoadingMore &&
          state.hasMore
        ) {
          fetchNotifications(
            state.currentPage + 1,
            true,
            state.typeFilter,
            state.unreadFilter
          )
        }
      },
      { threshold: 0.1, root: scrollRoot }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, isLoading, fetchNotifications])

  const handleMarkAsRead = useCallback(
    async (uuid: string) => {
      try {
        await notificationService.markAsRead(uuid)
        const markRead = (prev: NotificationListItem[]) =>
          prev.map((n) => (n.uuid === uuid ? { ...n, read: true } : n))
        setAllNotifications(markRead)

        if (unreadFilter) {
          setFilteredByTypeNotifications((prev) =>
            prev.filter((n) => n.uuid !== uuid)
          )
        } else {
          setFilteredByTypeNotifications(markRead)
        }

        setUnreadCount((prev) => Math.max(0, prev - 1))
      } catch (error) {
        console.error('Error marking notification as read:', error)
      }
    },
    [unreadFilter]
  )

  const handleMarkAllAsRead = useCallback(async () => {
    if (unreadCount === 0 || isMarkingAllRead) return

    setIsMarkingAllRead(true)
    try {
      await notificationService.markAllAsRead()
      const markAllRead = (prev: NotificationListItem[]) =>
        prev.map((n) => ({ ...n, read: true }))
      setAllNotifications(markAllRead)

      if (unreadFilter) {
        setFilteredByTypeNotifications([])
      } else {
        setFilteredByTypeNotifications(markAllRead)
      }

      setUnreadCount(0)
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    } finally {
      setIsMarkingAllRead(false)
    }
  }, [unreadCount, isMarkingAllRead, unreadFilter])

  const renderSkeletons = () => (
    <>
      {[1, 2, 3, 4].map((i) => (
        <S.SkeletonItem key={i}>
          <Skeleton
            variant="rounded"
            width={36}
            height={36}
            sx={{ borderRadius: '10px', flexShrink: 0 }}
          />
          <S.SkeletonContent>
            <Skeleton variant="text" width="75%" height={18} />
            <Skeleton variant="text" width="90%" height={15} />
            <Skeleton variant="text" width="35%" height={13} />
          </S.SkeletonContent>
        </S.SkeletonItem>
      ))}
    </>
  )

  const renderEmptyState = () => (
    <S.EmptyState>
      <S.EmptyStateIcon>
        <Icon
          icon="faBell"
          colorSX={theme.palette.neutral[500]}
          width="24px"
          height="24px"
        />
      </S.EmptyStateIcon>
      <Stack gap={0.5} alignItems="center">
        <Typography
          variant="smBold"
          sx={{ color: theme.palette.neutral[200], fontSize: '14px' }}
        >
          {typeFilter || unreadFilter
            ? unreadFilter && !typeFilter
              ? 'Nenhuma notificação não lida'
              : 'Nenhuma notificação nesta categoria'
            : 'Nenhuma notificação'}
        </Typography>
        <Typography
          variant="xsLight"
          sx={{
            color: theme.palette.neutral[450],
            fontSize: '13px',
            maxWidth: '220px',
            textAlign: 'center',
            lineHeight: 1.5
          }}
        >
          {typeFilter || unreadFilter
            ? 'Tente selecionar outra categoria ou limpe o filtro'
            : 'Você será notificado sobre eventos importantes aqui'}
        </Typography>
      </Stack>
    </S.EmptyState>
  )

  const handleOpenBillingDetail = useCallback((billingUuid: string) => {
    setIsOpen(false)
    setChargeDetailUuid(billingUuid)
  }, [])

  const handleCloseBillingDetail = useCallback(() => {
    setChargeDetailUuid(null)
  }, [])

  const handleOpenNfseDetail = useCallback((nfseUuid: string) => {
    setIsOpen(false)
    setNfseDetailUuid(nfseUuid)
  }, [])

  const handleOpenReport = useCallback(() => {
    setIsOpen(false)
    onOpenReportModal?.()
  }, [onOpenReportModal])

  const handleOpenBalanceModal = useCallback(
    (tab: string, disputeUuid?: string) => {
      setIsOpen(false)
      onOpenBalanceModal?.(tab, disputeUuid)
    },
    [onOpenBalanceModal]
  )

  const handleCloseNfseDetail = useCallback(() => {
    setNfseDetailUuid(null)
  }, [])

  const handleToggleTypeFilter = (type: TypeFilter) => {
    const newFilter = typeFilter === type ? null : type
    setTypeFilter(newFilter)
    setCurrentPage(1)
    setHasMore(false)

    if (newFilter || unreadFilter) {
      setFilteredByTypeNotifications([])
      fetchNotifications(1, false, newFilter, unreadFilter)
    }
  }

  const handleToggleUnreadFilter = () => {
    const newUnread = !unreadFilter
    setUnreadFilter(newUnread)
    setCurrentPage(1)
    setHasMore(false)

    if (newUnread || typeFilter) {
      setFilteredByTypeNotifications([])
      fetchNotifications(1, false, typeFilter, newUnread)
    }
  }

  // Clique no toast em tempo real: mesmo destino da central de notificações,
  // marcando como lido (igual ao NotificationItem) e abrindo o detalhe.
  toastClickRef.current = (notification: NotificationChannelData) => {
    if (!notification.read) {
      handleMarkAsRead(notification.uuid)
    }

    navigateFromNotification(
      {
        navigation_url: notification.navigation_url,
        event_type: notification.event_type
      },
      {
        router,
        onOpenBillingDetail: handleOpenBillingDetail,
        onOpenNfseDetail: handleOpenNfseDetail,
        onOpenReportModal: handleOpenReport,
        onOpenBalanceModal: handleOpenBalanceModal,
        onCloseDrawer: handleCloseDrawer
      }
    )
  }

  // Sem permissão não há o que mostrar: o sino abriria uma central que só
  // consegue responder 403. Depois dos hooks para não quebrar a ordem deles.
  if (!canViewNotifications) {
    return null
  }

  return (
    <S.Wrapper>
      <Tooltip title="Notificações" disableHoverListener={isOpen}>
        <IconButton onMouseDown={handleToggleDrawer}>
          <S.StyledBadge
            badgeContent={unreadCount > 99 ? '99+' : unreadCount}
            invisible={unreadCount === 0}
          >
            <Icon
              height="21px"
              width="21px"
              icon="faBell"
              colorSX={theme.palette.neutral[400]}
            />
          </S.StyledBadge>
        </IconButton>
      </Tooltip>

      <Drawer
        open={isOpen}
        onClose={handleToggleDrawer}
        anchor="right"
        ModalProps={{ disableEscapeKeyDown: isSettingsOpen }}
        PaperProps={{ sx: { width: '520px', maxWidth: '100%' } }}
      >
        <DrawerBody>
          <DrawerHeader onClose={handleToggleDrawer}>
            <S.DrawerHeaderWrapper>
              <S.HeaderLeft>
                <Typography
                  variant="mdBold"
                  sx={{ color: theme.palette.neutral[200] }}
                >
                  Notificações
                </Typography>
                {unreadCount > 0 && (
                  <S.UnreadBadge>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </S.UnreadBadge>
                )}
              </S.HeaderLeft>

              <S.HeaderActions>
                {unreadCount > 0 && (
                  <Tooltip title="Marcar todas como lidas">
                    <S.MarkAllReadButton
                      onClick={handleMarkAllAsRead}
                      disabled={isMarkingAllRead}
                      aria-label="Marcar todas como lidas"
                    >
                      <Icon
                        icon="doneAll"
                        colorSX={theme.palette.primary[100]}
                        width="14px"
                        height="14px"
                      />
                      <span className="mark-all-label">
                        Marcar todas como lidas
                      </span>
                    </S.MarkAllReadButton>
                  </Tooltip>
                )}
                <Tooltip title="Configurações de Notificações">
                  <IconButton
                    onClick={handleOpenSettings}
                    size="small"
                    aria-label="Configurações de Notificações"
                    sx={{
                      width: 32,
                      height: 32,
                      '&:hover': {
                        backgroundColor: theme.palette.neutral[800]
                      }
                    }}
                  >
                    <Icon
                      icon="settings"
                      colorSX={theme.palette.neutral[400]}
                      width="17px"
                      height="17px"
                    />
                  </IconButton>
                </Tooltip>
              </S.HeaderActions>
            </S.DrawerHeaderWrapper>
          </DrawerHeader>

          <S.FiltersWrapper>
            <S.FilterChip
              isActive={unreadFilter}
              chipColor={theme.palette.error[300]}
              onClick={handleToggleUnreadFilter}
            >
              Não lidas
              {unreadFilter && (
                <Icon
                  icon="close"
                  colorSX={theme.palette.error[300]}
                  width="12px"
                  height="12px"
                />
              )}
            </S.FilterChip>
            {TYPE_FILTER_OPTIONS.map((opt) => (
              <S.FilterChip
                key={opt.key}
                isActive={typeFilter === opt.key}
                chipColor={opt.color}
                onClick={() => handleToggleTypeFilter(opt.key)}
              >
                {opt.label}
                {typeFilter === opt.key && (
                  <Icon
                    icon="close"
                    colorSX={opt.color}
                    width="12px"
                    height="12px"
                  />
                )}
              </S.FilterChip>
            ))}
          </S.FiltersWrapper>

          <DrawerContent>
            <S.NotificationsList>
              {isLoading && renderSkeletons()}

              {!isLoading && notifications.length === 0 && renderEmptyState()}

              {!isLoading &&
                groupedNotifications.map((group) => (
                  <S.DateGroup key={group.label}>
                    <S.DateLabel>{group.label}</S.DateLabel>
                    {group.items.map((notification) => (
                      <NotificationItem
                        key={notification.uuid}
                        uuid={notification.uuid}
                        title={notification.title}
                        body={notification.body}
                        notification_type={notification.notification_type}
                        read={notification.read}
                        icon={notification.icon}
                        navigation_url={notification.navigation_url}
                        event_type={notification.event_type}
                        created_at={notification.created_at}
                        merchant={notification.merchant}
                        onMarkAsRead={handleMarkAsRead}
                        // SC-10999: o detalhe aberto da notificação busca e
                        // age na empresa dela
                        onOpenBillingDetail={(billingUuid) => {
                          registerRecordMerchant(
                            billingUuid,
                            notification.merchant?.uuid
                          )
                          handleOpenBillingDetail(billingUuid)
                        }}
                        onOpenNfseDetail={(nfseUuid) => {
                          registerRecordMerchant(
                            nfseUuid,
                            notification.merchant?.uuid
                          )
                          handleOpenNfseDetail(nfseUuid)
                        }}
                        onOpenReportModal={handleOpenReport}
                        onOpenBalanceModal={handleOpenBalanceModal}
                        onCloseDrawer={handleCloseDrawer}
                      />
                    ))}
                  </S.DateGroup>
                ))}

              {!isLoading && hasMore && (
                <S.ScrollSentinel ref={loadMoreRef}>
                  {isLoadingMore && (
                    <>
                      {[1, 2].map((i) => (
                        <S.SkeletonItem key={i}>
                          <Skeleton
                            variant="rounded"
                            width={36}
                            height={36}
                            sx={{ borderRadius: '10px', flexShrink: 0 }}
                          />
                          <S.SkeletonContent>
                            <Skeleton variant="text" width="75%" height={18} />
                            <Skeleton variant="text" width="90%" height={15} />
                          </S.SkeletonContent>
                        </S.SkeletonItem>
                      ))}
                    </>
                  )}
                </S.ScrollSentinel>
              )}
            </S.NotificationsList>
          </DrawerContent>
        </DrawerBody>
      </Drawer>

      <Dialog
        open={isSettingsOpen}
        onClose={handleCloseSettings}
        maxWidth="md"
        fullWidth
        sx={{ zIndex: 1400 }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 2.5,
              py: 3.5,
              borderBottom: `1px solid ${theme.palette.neutral[700]}`
            }}
          >
            <Stack direction="row" alignItems="center" gap={1.5}>
              <Icon
                icon="settings"
                colorSX={theme.palette.neutral[300]}
                width="22px"
                height="22px"
              />
              <Stack gap={0.25}>
                <Typography
                  variant="mdBold"
                  sx={{ color: theme.palette.neutral[200] }}
                >
                  Configurações de Notificações
                </Typography>
                <Typography
                  variant="xsLight"
                  sx={{
                    color: theme.palette.neutral[400],
                    fontSize: '13px'
                  }}
                >
                  Gerencie suas preferências de notificação
                </Typography>
              </Stack>
            </Stack>
            <IconButton
              onClick={handleCloseSettings}
              size="small"
              sx={{
                width: 36,
                height: 36,
                '&:hover': {
                  backgroundColor: theme.palette.neutral[800]
                }
              }}
            >
              <Icon
                icon="close"
                colorSX={theme.palette.neutral[400]}
                width="18px"
                height="18px"
              />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 2.5,
            maxHeight: '60vh',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px'
            },
            '&::-webkit-scrollbar-thumb': {
              background: theme.palette.neutral[600],
              borderRadius: '3px'
            }
          }}
        >
          {isSettingsOpen && (
            <NotificationSettings
              onClose={handleCloseSettings}
              onHasChangesChange={setSettingsHasChanges}
              onSaveCallbackChange={(cb) => setSaveSettingsCallback(() => cb)}
            />
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 2.5,
            pt: 2,
            borderTop: `1px solid ${theme.palette.neutral[700]}`,
            gap: 1.5
          }}
        >
          <Button
            variantButton="primary"
            onClick={handleSaveSettings}
            disabled={!settingsHasChanges || settingsIsSaving}
            loading={settingsIsSaving}
          >
            Salvar preferências
          </Button>
        </DialogActions>
      </Dialog>
      {chargeDetailUuid && (
        <ModalChargeDetail
          uuid={chargeDetailUuid}
          isOpenChargeDetail={!!chargeDetailUuid}
          handleCloseChargeDetail={handleCloseBillingDetail}
        />
      )}

      {nfseDetailUuid && (
        <Modal
          handleClose={handleCloseNfseDetail}
          open={!!nfseDetailUuid}
          width="100%"
          maxWidth="85vw"
          maxHeight="90vh"
        >
          <ModalInvoiceDetailsContent
            uuid={nfseDetailUuid}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            handleReloadTable={() => {}}
            handleCloseModal={handleCloseNfseDetail}
          />
        </Modal>
      )}
    </S.Wrapper>
  )
}

export default memo(NotificationCenter)
